import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Shield, ExternalLink } from 'lucide-react';
import { TranslatedText } from '../ui';
import { useLandingContent } from '../../hooks/useLandingContent';

interface Partner {
  id: string;
  name: string;
  country: string;
  logo: string;
  link: string;
}

const COUNTRY_META: Record<string, { name: string; flag: string }> = {
  ARG: { name: 'Argentina', flag: '🇦🇷' },
  BOL: { name: 'Bolivia', flag: '🇧🇴' },
  BRA: { name: 'Brasil', flag: '🇧🇷' },
  CHL: { name: 'Chile', flag: '🇨🇱' },
  COL: { name: 'Colombia', flag: '🇨🇴' },
  ECU: { name: 'Ecuador', flag: '🇪🇨' },
  ESP: { name: 'España', flag: '🇪🇸' },
  ITA: { name: 'Italia', flag: '🇮🇹' },
  MEX: { name: 'México', flag: '🇲🇽' },
  PAR: { name: 'Paraguay', flag: '🇵🇾' },
  PER: { name: 'Perú', flag: '🇵🇪' },
  POR: { name: 'Portugal', flag: '🇵🇹' },
  URU: { name: 'Uruguay', flag: '🇺🇾' },
  USA: { name: 'Estados Unidos', flag: '🇺🇸' },
  VEN: { name: 'Venezuela', flag: '🇻🇪' },
};

const FALLBACK_PARTNERS: Omit<Partner, 'id'>[] = [
  { 
    name: 'Argentinos Juniors', 
    country: 'ARG',
    logo: '/images/clubs/argentinos-juniors.png',
    link: 'https://argentinosjuniors.com.ar/'
  },
  { 
    name: 'Deportivo Cali', 
    country: 'COL',
    logo: '/images/clubs/deportivo-cali.png',
    link: 'https://deportivocali.com.co/'
  },
  { 
    name: 'Defensa y Justicia', 
    country: 'ARG',
    logo: '/images/clubs/defensa-y-justicia.png',
    link: 'http://defensayjusticia.org.ar/'
  },
  { 
    name: 'Universidad de Chile', 
    country: 'CHL',
    logo: '/images/clubs/universidad-de-chile.png',
    link: 'https://www.udechile.cl/'
  },
  { 
    name: 'Santiago Morning', 
    country: 'CHL',
    logo: '/images/clubs/santiago-morning.png',
    link: 'https://www.santiagomorning.cl/'
  },
  { 
    name: "O'Higgins F.C.", 
    country: 'CHL',
    logo: '/images/clubs/ohiggins.png',
    link: 'https://www.ohigginsfc.cl/'
  },
  { 
    name: 'Universidad Católica', 
    country: 'CHL',
    logo: '/images/clubs/universidad-catolica.png',
    link: 'https://cruzados.cl/'
  },
  { 
    name: 'Santiago Wanderers', 
    country: 'CHL',
    logo: '/images/clubs/santiago-wanderers.png',
    link: 'https://santiagowanderers.cl/'
  },
  { 
    name: 'Audax Italiano', 
    country: 'CHL',
    logo: '/images/clubs/audax-italiano.png',
    link: 'https://www.audaxitaliano.cl/'
  },
  { 
    name: 'Barnechea', 
    country: 'CHL',
    logo: '/images/clubs/barnechea.png',
    link: 'https://acbarnechea.cl/'
  },
  { 
    name: 'U. de Concepción', 
    country: 'CHL',
    logo: '/images/clubs/universidad-de-concepcion.png',
    link: 'https://www.cdudec.cl/'
  },
  { 
    name: 'Ñublense', 
    country: 'CHL',
    logo: '/images/clubs/nublense.png',
    link: 'https://losdiablosrojos.cl/'
  },
  { 
    name: 'Palestino', 
    country: 'CHL',
    logo: '/images/clubs/palestino.png',
    link: 'https://palestino.cl/'
  },
  { 
    name: 'San Luis', 
    country: 'CHL',
    logo: '/images/clubs/san-luis.png',
    link: 'https://www.sanluissa.cl/'
  },
  { 
    name: 'Club Atlético Paso', 
    country: 'ESP',
    logo: '/images/clubs/atletico-paso.png',
    link: 'https://atleticopaso.club/'
  },
  { 
    name: 'Deportes Recoleta', 
    country: 'CHL',
    logo: '/images/clubs/deportes-recoleta.png',
    link: 'https://deportesrecoletasadp.cl/club/'
  },
  { 
    name: 'Trasandino', 
    country: 'CHL',
    logo: '/images/clubs/trasandino.png',
    link: 'https://www.cdtrasandino.cl/'
  },
  { 
    name: 'Sport Boys', 
    country: 'PER',
    logo: '/images/clubs/sport-boys.png',
    link: 'https://www.sportboys.com.pe/'
  },
  { 
    name: 'Unión Española', 
    country: 'CHL',
    logo: '/images/clubs/union-espanola.png',
    link: 'https://unionespanola.cl/'
  },
  { 
    name: 'Coquimbo Unido', 
    country: 'CHL',
    logo: '/images/clubs/coquimbo-unido.png',
    link: 'https://www.coquimbounido.cl/'
  },
  { 
    name: 'Sporting Cristal', 
    country: 'PER',
    logo: '/images/clubs/sporting-cristal.png',
    link: 'https://www.clubsportingcristal.pe/'
  },
  { 
    name: 'Concón National', 
    country: 'CHL',
    logo: '/images/clubs/concon-national.png',
    link: 'https://www.instagram.com/conconnationalfc/'
  },
  { 
    name: 'Everton', 
    country: 'CHL',
    logo: '/images/clubs/everton.png',
    link: 'https://www.tiendaeverton.cl/index.php'
  },
  { 
    name: 'Huachipato FC', 
    country: 'CHL',
    logo: '/images/clubs/huachipato.png',
    link: 'https://huachipatofc.cl/'
  },
  { 
    name: 'Deportes Concepción', 
    country: 'CHL',
    logo: '/images/clubs/deportes-concepcion.png',
    link: 'https://elconce.cl/'
  },
  { 
    name: 'Cultural Leonesa', 
    country: 'ESP',
    logo: '/images/clubs/cultural-leonesa.png',
    link: 'https://cydleonesa.com/'
  },
  {
    name: 'Mineros de Zacatecas',
    country: 'MEX',
    logo: '/images/clubs/mineros-zacatecas.png',
    link: 'https://www.minerosfc.com/'
  },
  {
    name: 'Cancún FC',
    country: 'MEX',
    logo: '/images/clubs/cancun-fc.png',
    link: 'https://www.cancunfc.com/'
  },
  {
    name: 'Club de Fútbol Pachuca',
    country: 'MEX',
    logo: '/images/clubs/pachuca.png',
    link: 'https://tuzos.com.mx/'
  },
];

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

// Animated team card component
function TeamCard({ partner, index }: { partner: Partner; index: number }) {
  const [isHovered, setIsHovered] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const showLogo = Boolean(partner.logo) && !logoBroken;

  return (
    <motion.a
      href={partner.link}
      target="_blank"
      rel="noopener noreferrer"
      layout
      initial={{ opacity: 0, scale: 0.5, rotateY: -90 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      exit={{ opacity: 0, scale: 0.5, rotateY: 90 }}
      transition={{ 
        type: 'spring',
        stiffness: 300,
        damping: 25,
        delay: index * 0.03,
      }}
      whileHover={{ scale: 1.08, y: -8, zIndex: 20 }}
      whileTap={{ scale: 0.95 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative group perspective-1000"
    >
      <motion.div 
        className="relative aspect-square bg-gradient-to-br from-brand-blue/80 to-brand-navy/90 backdrop-blur-xl border border-brand-lightBlue/30 rounded-2xl p-4 flex flex-col items-center justify-center overflow-hidden"
        animate={{
          borderColor: isHovered ? 'rgba(255, 215, 0, 0.6)' : 'rgba(42, 64, 96, 0.3)',
          boxShadow: isHovered 
            ? '0 20px 40px -10px rgba(255, 215, 0, 0.3), 0 0 60px -15px rgba(255, 215, 0, 0.2)'
            : '0 4px 20px -5px rgba(0, 0, 0, 0.3)',
        }}
        transition={{ duration: 0.3 }}
      >
        {/* Animated background glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-radial from-brand-yellow/20 to-transparent"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ 
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1.5 : 0.5,
          }}
          transition={{ duration: 0.4 }}
        />

        {/* Shimmer effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: isHovered ? '100%' : '-100%' }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        />
        
        {/* Team logo with float animation */}
        <motion.div 
          className="relative w-16 h-16 md:w-20 md:h-20 mb-2 flex items-center justify-center z-10"
          animate={{
            y: isHovered ? -5 : 0,
            scale: isHovered ? 1.1 : 1,
            rotateZ: isHovered ? [0, -3, 3, 0] : 0,
          }}
          transition={{
            y: { duration: 0.3 },
            scale: { duration: 0.3 },
            rotateZ: { duration: 0.5, repeat: isHovered ? Infinity : 0, repeatType: 'reverse' },
          }}
        >
          {showLogo ? (
            <img
              src={partner.logo}
              alt={partner.name}
              className="w-full h-full object-contain drop-shadow-lg"
              loading="lazy"
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <div
              aria-label={partner.name}
              className="w-full h-full rounded-full bg-brand-lightBlue/20 border border-brand-yellow/30 flex items-center justify-center"
            >
              <span className="text-sm md:text-base font-black text-brand-yellow">
                {initialsOf(partner.name) || <Shield className="w-5 h-5" />}
              </span>
            </div>
          )}
        </motion.div>
        
        {/* Team name with slide up animation */}
        <motion.span 
          className="relative text-[10px] md:text-xs text-center text-dark-200 font-semibold leading-tight line-clamp-2 z-10"
          animate={{ 
            color: isHovered ? '#ffffff' : '#94a3b8',
            y: isHovered ? -2 : 0,
          }}
          transition={{ duration: 0.2 }}
        >
          {partner.name}
        </motion.span>
        
        {/* Country badge - animated */}
        <motion.div 
          className="absolute -top-1 -right-1 px-2 py-1 rounded-lg bg-gradient-to-r from-brand-yellow to-brand-orange text-[9px] font-black text-brand-darkNavy shadow-lg"
          initial={{ scale: 0, rotate: -45 }}
          animate={{ 
            scale: isHovered ? 1 : 0,
            rotate: isHovered ? 0 : -45,
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
        >
          {partner.country || '—'}
        </motion.div>

        {/* External link icon */}
        <motion.div
          className="absolute bottom-2 right-2"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <ExternalLink className="w-3 h-3 text-brand-yellow" />
        </motion.div>

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-yellow/30 rounded-tl-lg" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-yellow/30 rounded-br-lg" />
      </motion.div>
    </motion.a>
  );
}

export function Partners() {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const { data } = useLandingContent();

  // Never drop a partner the CMS returned: country and logo are best-effort
  // display data, so a blank or unknown value degrades instead of filtering.
  const partners: Partner[] = useMemo(
    () =>
      data?.partners?.length
        ? data.partners.map((p) => ({
            id: p.id,
            name: p.name,
            country: (p.country ?? '').trim().toUpperCase(),
            logo: p.logo_url ?? '',
            link: p.link || '#',
          }))
        : FALLBACK_PARTNERS.map((p) => ({ ...p, id: p.name })),
    [data],
  );

  const countByCountry = useMemo(() => {
    const counts: Record<string, number> = { ALL: partners.length };
    partners.forEach(p => {
      counts[p.country] = (counts[p.country] || 0) + 1;
    });
    return counts;
  }, [partners]);

  // Derived from the partners themselves so any ISO code set in the admin gets
  // its own filter, and countries with no clubs left disappear.
  const countries = useMemo(() => {
    const codes = Array.from(new Set(partners.map(p => p.country).filter(Boolean)))
      .sort((a, b) => (countByCountry[b] ?? 0) - (countByCountry[a] ?? 0) || a.localeCompare(b));

    return [
      { code: 'ALL', name: t('partners.filterAll'), flag: '🌎' },
      ...codes.map(code => ({
        code,
        name: COUNTRY_META[code]?.name ?? code,
        flag: COUNTRY_META[code]?.flag ?? '🏳️',
      })),
    ];
  }, [partners, countByCountry, t]);

  const filteredPartners = useMemo(() => {
    if (selectedCountry === 'ALL') return partners;
    return partners.filter(p => p.country === selectedCountry);
  }, [selectedCountry, partners]);

  return (
    <section id="partners" className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/60 backdrop-blur-sm" />
      
      {/* Animated glow effects */}
      <motion.div 
        className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-brand-yellow/5 rounded-full blur-[150px]"
        animate={{
          x: [0, 50, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-brand-yellow/5 rounded-full blur-[100px]"
        animate={{
          x: [0, -30, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(255, 215, 0, 0.1)',
                '0 0 40px rgba(255, 215, 0, 0.2)',
                '0 0 20px rgba(255, 215, 0, 0.1)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Shield className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">{t('partners.badge')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText 
              i18nKey="partners.title" 
              type="digital"
              duration={0.8}
            />
          </h2>
          <p className="text-lg md:text-xl text-dark-300 mb-4">
            <TranslatedText 
              i18nKey="partners.subtitle" 
              type="fadeWords"
              duration={0.4}
              stagger={0.04}
            />
          </p>
          <p className="text-brand-yellow font-semibold">
            <TranslatedText i18nKey="partners.tagline" type="fade" duration={0.4} />
          </p>
        </motion.div>

        {/* Country Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12"
        >
          {countries.map((country, i) => {
            const isActive = selectedCountry === country.code;
            const count = countByCountry[country.code] || 0;
            
            return (
              <motion.button
                key={country.code}
                onClick={() => setSelectedCountry(country.code)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  relative flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm
                  transition-all duration-300 overflow-hidden
                  ${isActive 
                    ? 'bg-gradient-to-r from-brand-yellow to-brand-orange text-brand-darkNavy shadow-lg shadow-brand-yellow/30' 
                    : 'bg-brand-blue/60 border border-brand-lightBlue/30 text-dark-200 hover:border-brand-yellow/50 hover:text-white'
                  }
                `}
              >
                {/* Animated background for active state */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-brand-yellow via-brand-orange to-brand-yellow"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ opacity: 0.3 }}
                  />
                )}
                
                <span className="text-base">{country.flag}</span>
                <span className="relative z-10">{country.code}</span>
                
                {/* Count badge */}
                <motion.span 
                  className={`
                    relative z-10 text-xs px-1.5 py-0.5 rounded-full
                    ${isActive ? 'bg-brand-darkNavy/30 text-white' : 'bg-brand-yellow/20 text-brand-yellow'}
                  `}
                  animate={{ scale: isActive ? [1, 1.1, 1] : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {count}
                </motion.span>
              </motion.button>
            );
          })}
        </motion.div>

        {/* Partners Grid with AnimatePresence for smooth filtering */}
        <motion.div 
          layout
          className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 md:gap-4"
        >
          <AnimatePresence mode="popLayout">
            {filteredPartners.map((partner, index) => (
              <TeamCard 
                key={partner.id} 
                partner={partner} 
                index={index}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Result count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-8"
        >
          <motion.p 
            key={filteredPartners.length}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-dark-400 text-sm"
          >
            {t('partners.showing')} <span className="text-brand-yellow font-bold">{filteredPartners.length}</span> {t('partners.teamsLabel')}
            {selectedCountry !== 'ALL' && (
              <> {t('partners.teamsFrom')} <span className="text-white font-semibold">{countries.find(c => c.code === selectedCountry)?.name}</span></>
            )}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
